import {
  inject,
  ApplicationConfig,
  provideAppInitializer,
  provideZoneChangeDetection
} from '@angular/core';
import { provideRouter } from '@angular/router';

import { routes } from './app.routes';
import {
  HttpClient,
  provideHttpClient,
  withInterceptors
} from '@angular/common/http';
import { provideToastr } from 'ngx-toastr';
import { errorInterceptor } from './core/interceptors/error.interceptor';
import { provideAnimations } from '@angular/platform-browser/animations';
import { CurrencyPipe } from '@angular/common';
import { wooCommerceCartTokenInterceptor } from './core/interceptors/woo-commerce-cart-token.interceptor';
import { withNgxsReduxDevtoolsPlugin } from '@ngxs/devtools-plugin';
import { provideStore } from '@ngxs/store';
import { firstValueFrom } from 'rxjs';

// State
import { DeliveryState } from './shared/state/delivery/delivery.state';
import { withNgxsStoragePlugin } from '@ngxs/storage-plugin';
import { CartState } from './shared/state/cart/cart.state';
import { RestaurantState } from './shared/state/restaurant/restaurant.state';
import { ShippingState } from './shared/state/shipping/shipping.state';
import { OrdersState } from './shared/state/orders/orders.state';
import { AuthState } from './shared/state/auth/auth.state';
import { UserProfileState } from './shared/state/user-profile/user-profile.state';
import { jwtAuthInterceptor } from './core/interceptors/jwt-auth.interceptor';
import { INITIAL_BACKEND_VARIABLES } from './core/constants/initial-backend-styles.constant';
import { CustomThemeService } from './core/services/custom-theme.service';
import { GoogleAnalyticsService } from './core/services/google-analytics.service';
import { happyApiHeadersInterceptor } from './core/interceptors/happy-api-headers.interceptor';
import { HappyTenantContextService } from './core/services/happy/happy-tenant-context.service';
import { HappyResolveApiService } from './core/services/happy/happy-resolve-api.service';
import { HappyStoreSelectionService } from './core/services/happy/happy-store-selection.service';
import { HappyStoreDisabledService } from './core/services/happy/happy-store-disabled.service';
import { HappyStoresApiService } from './core/services/happy/happy-stores-api.service';
import { STORAGE_ENGINE } from '@ngxs/storage-plugin';
import { HappyStoreLocalStorageEngine } from './core/services/happy/happy-store-local-storage.engine';
import { environment } from '../environments/environment';
import { ThemeManifest } from './core/interfaces/theme-config.interface';
import { CacheVersionsReconcileService } from './core/services/cache-versions/cache-versions-reconcile.service';

export async function initEnvironmentTheme() {
  const themeService = inject(CustomThemeService);
  return themeService.initializeTheme();
}

export function initGoogleAnalytics() {
  const gaService = inject(GoogleAnalyticsService);
  gaService.initialize();
}

export function initBackendVars() {
  const root: HTMLElement = document.documentElement;

  for (const [key, value] of Object.entries(INITIAL_BACKEND_VARIABLES)) {
    root.style.setProperty(key, value);
  }
}

export function initSmartAppBanner() {
  const hasBannerConfig = !!document.querySelector(
    'meta[name="smartbanner:enabled-platforms"]'
  );

  if (hasBannerConfig) {
    import('smartbanner.js');
  }
}

export function initHappyTenantContext(): void {
  inject(HappyTenantContextService).bootstrap();
}

export function initHappyStoreSelection(): void {
  inject(HappyStoreSelectionService).bootstrapSelectionContext();
}

export async function initStoresList(): Promise<void> {
  const storesApi = inject(HappyStoresApiService);
  const storeValidation = inject(HappyStoreDisabledService);
  const tenantContext = inject(HappyTenantContextService);
  const storeSelection = inject(HappyStoreSelectionService);

  try {
    const stores = await firstValueFrom(
      storesApi.fetchActiveInstantOrderingStores()
    );
    storeValidation.setActiveStores(stores);

    if (stores.length === 0) {
      return;
    }

    const savedApiStore = storeSelection.readApiStoreSelection();
    if (savedApiStore) {
      const savedMatch = stores.find(
        (s) => s.domain.toLowerCase() === savedApiStore.domain.toLowerCase()
      );
      if (savedMatch) {
        tenantContext.setSelectedStore({
          storeName: savedMatch.domain,
          storeApiKey: savedMatch.api_key
        });
        return;
      }
      storeSelection.clearApiStoreSelection();
    }

    const currentDomain = tenantContext.storeName();
    const currentIsActive = stores.some(
      (s) => s.domain.toLowerCase() === currentDomain.toLowerCase()
    );

    if (currentIsActive) {
      const matched = stores.find(
        (s) => s.domain.toLowerCase() === currentDomain.toLowerCase()
      );
      if (matched && !tenantContext.storeApiKey()) {
        tenantContext.setApiKey(matched.api_key);
      }
      return;
    }

    const firstActive = stores[0];
    storeSelection.selectByDomainWithApiKey(
      firstActive.domain,
      firstActive.api_key
    );
    storeValidation.markRedirected();

    if (typeof window !== 'undefined' && currentDomain) {
      const segments = window.location.pathname.split('/').filter(Boolean);
      if (
        segments.length > 0 &&
        segments[0].toLowerCase() === currentDomain.toLowerCase()
      ) {
        segments[0] = firstActive.domain;
        const newPath = '/' + segments.join('/');
        window.history.replaceState(
          null,
          '',
          newPath + window.location.search + window.location.hash
        );
      }
    }
  } catch (error) {
    console.warn('[StoresList] Failed to validate stores:', error);
  }
}

export async function initHappyApiKey(): Promise<void> {
  const ctx = inject(HappyTenantContextService);

  if (ctx.storeApiKey()) {
    return;
  }

  const tenant = ctx.tenant();
  const storeName = ctx.storeName();
  if (!tenant || !storeName) {
    console.warn(
      '[HappyTenantContext] Cannot resolve api_key: missing tenant or storeName',
      { tenant, storeName }
    );
    return;
  }

  try {
    const resolve = inject(HappyResolveApiService);
    const resolved = await firstValueFrom(resolve.resolve(storeName, tenant));
    if (resolved?.api_key) {
      ctx.setApiKey(resolved.api_key);
    }
  } catch (error) {
    console.error(
      `[HappyTenantContext] Failed to resolve api_key for ${tenant}/${storeName}:`,
      error
    );
  }
}

export function initCacheVersionsReconcile(): void {
  // Fire-and-forget: must not block app boot. Failures are swallowed by the
  // reconcile service itself (see CacheVersionsApiService.getCacheVersions).
  console.log('[cache-versions] initializer fired');
  const service = inject(CacheVersionsReconcileService);
  service.reconcile().then(
    () => console.log('[cache-versions] reconcile finished'),
    (e) => console.warn('[cache-versions] reconcile threw', e)
  );
}

export async function initTenantManifest(): Promise<void> {
  const themeService = inject(CustomThemeService);
  const http = inject(HttpClient);
  const ctx = inject(HappyTenantContextService);

  const tenant = ctx.tenant();
  const storeName = ctx.storeName();

  const candidates: string[] = [];
  if (tenant && storeName) {
    candidates.push(`/assets/manifests/${tenant}.${storeName}.json`);
  }
  if (tenant) {
    candidates.push(`/assets/manifests/${tenant}.json`);
  }
  candidates.push('/assets/manifests/manifest.default.json');

  for (const url of candidates) {
    try {
      const manifest = await firstValueFrom(http.get<ThemeManifest>(url));
      themeService.applyManifest(manifest);
      return;
    } catch (error) {
      if (url === candidates[candidates.length - 1]) {
        console.warn(
          '[HappyTenantContext] Failed to load any tenant manifest, keeping env defaults.',
          error
        );
      }
    }
  }
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideAppInitializer(initHappyTenantContext),
    provideAppInitializer(initHappyStoreSelection),
    provideAppInitializer(initStoresList),
    provideAppInitializer(initHappyApiKey),
    provideAppInitializer(initCacheVersionsReconcile),
    provideAppInitializer(initEnvironmentTheme),
    provideAppInitializer(initTenantManifest),
    provideAppInitializer(initBackendVars),
    provideAppInitializer(initGoogleAnalytics),
    provideAppInitializer(initSmartAppBanner),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideHttpClient(
      withInterceptors([
        jwtAuthInterceptor,
        happyApiHeadersInterceptor,
        errorInterceptor,
        wooCommerceCartTokenInterceptor
      ])
    ),
    provideAnimations(),
    provideToastr({
      timeOut: 3000,
      positionClass: 'toast-top-right',
      preventDuplicates: true
    }),
    CurrencyPipe,
    {
      provide: STORAGE_ENGINE,
      useClass: HappyStoreLocalStorageEngine
    },
    provideStore(
      [
        AuthState,
        DeliveryState,
        CartState,
        RestaurantState,
        ShippingState,
        OrdersState,
        UserProfileState
      ],
      withNgxsStoragePlugin({ keys: ['delivery', 'shipping', 'orders', 'auth'] }),
      withNgxsReduxDevtoolsPlugin()
    )
  ]
};
